import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, AlertTriangle, Info } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

interface PMICoverageBreakdownProps {
  homePrice: number;
  downPayment: number;
  monthlyPMI: number;
  loanType: string;
  creditScore?: number;
}

/**
 * Get PMI coverage percentage based on LTV ratio.
 * Standard coverage: 25% for LTV ≤ 90%, 30% for LTV > 90%.
 */
const getPMICoveragePercent = (ltv: number): number => {
  if (ltv > 95) return 35;
  if (ltv > 90) return 30;
  return 25;
};

export const PMICoverageBreakdown: React.FC<PMICoverageBreakdownProps> = ({
  homePrice,
  downPayment,
  monthlyPMI,
  loanType,
  creditScore,
}) => {
  const loanAmount = homePrice - downPayment;
  const downPaymentPercent = (downPayment / homePrice) * 100;
  const ltv = (loanAmount / homePrice) * 100;
  const coveragePercent = getPMICoveragePercent(ltv);
  const pmiCoverageAmount = loanAmount * (coveragePercent / 100);
  const lenderRisk = loanAmount - pmiCoverageAmount;
  const annualPMI = monthlyPMI * 12;

  // Bar segment widths as percentages of home price
  const dpWidth = (downPayment / homePrice) * 100;
  const coverageWidth = (pmiCoverageAmount / homePrice) * 100;
  const riskWidth = (lenderRisk / homePrice) * 100;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield className="w-5 h-5 text-primary" />
          PMI Coverage Breakdown
          <Badge variant="outline" className="ml-auto text-xs capitalize">
            {loanType === 'fha' ? 'MIP' : 'PMI'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Stacked Bar Visualization */}
        <div className="space-y-2">
          <div className="flex rounded-lg overflow-hidden h-8 border border-border">
            {/* Down Payment */}
            <div
              className="flex items-center justify-center text-xs font-semibold"
              style={{
                width: `${dpWidth}%`,
                backgroundColor: 'hsl(var(--primary))',
                color: 'hsl(var(--primary-foreground))',
                minWidth: dpWidth > 3 ? undefined : '24px',
              }}
            >
              {dpWidth >= 8 && `${downPaymentPercent.toFixed(0)}%`}
            </div>
            {/* PMI Coverage */}
            <div
              className="flex items-center justify-center text-xs font-semibold"
              style={{
                width: `${coverageWidth}%`,
                backgroundColor: 'hsl(var(--secondary))',
                color: 'hsl(var(--secondary-foreground))',
              }}
            >
              {coverageWidth >= 10 && 'PMI Coverage'}
            </div>
            {/* Lender Risk */}
            <div
              className="flex items-center justify-center text-xs font-semibold"
              style={{
                width: `${riskWidth}%`,
                backgroundColor: 'hsl(var(--muted))',
                color: 'hsl(var(--muted-foreground))',
              }}
            >
              {riskWidth >= 12 && "Lender's Risk"}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'hsl(var(--primary))' }} />
              Down Payment
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'hsl(var(--secondary))' }} />
              PMI Coverage
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: 'hsl(var(--muted))' }} />
              Lender's Risk
            </span>
          </div>
        </div>

        {/* Breakdown Table */}
        <div className="space-y-2.5 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Sales Price</span>
            <span className="font-semibold">{formatCurrency(homePrice)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Down Payment ({downPaymentPercent.toFixed(1)}%)</span>
            <span className="font-semibold text-primary">{formatCurrency(downPayment)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Loan Amount ({ltv.toFixed(1)}% LTV)</span>
            <span className="font-semibold">{formatCurrency(loanAmount)}</span>
          </div>

          <div className="border-t border-border my-1" />

          <div className="flex justify-between">
            <span className="text-muted-foreground">PMI Coverage ({coveragePercent}% of loan)</span>
            <span className="font-semibold text-secondary">{formatCurrency(pmiCoverageAmount)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground flex items-center gap-1">
              Lender's Risk
              <AlertTriangle className="w-3.5 h-3.5" />
            </span>
            <span className="font-semibold">{formatCurrency(lenderRisk)}</span>
          </div>

          <div className="border-t border-border my-1" />

          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Monthly {loanType === 'fha' ? 'MIP' : 'PMI'} Premium</span>
            <span className="font-bold text-destructive">{formatCurrency(monthlyPMI)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Annual Premium</span>
            <span className="font-semibold">{formatCurrency(annualPMI)}</span>
          </div>
        </div>

        {/* Info note */}
        <div className="flex gap-2 p-3 rounded-lg text-xs text-muted-foreground" style={{ backgroundColor: 'hsl(var(--muted))' }}>
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            PMI protects the <strong>lender</strong>, not you. It covers {coveragePercent}% of the loan if you default.
            PMI is removed once your LTV reaches 80% (equity ≥ 20%).
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PMICoverageBreakdown;
